import React, { useCallback, useEffect, useRef, useState } from 'react';
import { copyToClipboard } from './copyToClipboard';
import type { CopyToastState } from './CopyToast';

const SKIP_SELECTOR =
  'button, a, input, textarea, svg, [role="button"], [contenteditable="true"]';
const MAX_COPY_LENGTH = 500;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;
const TOAST_TTL_MS = 1100;

/**
 * Wires double-click (desktop) and double-tap (mobile) on a scroll container to
 * clipboard copy + transient toast. Skips interactive descendants so buttons,
 * links, and form controls keep their native behavior.
 */
export function useCopyOnDoubleClick() {
  const [toast, setToast] = useState<CopyToastState>(null);
  const lastTap = useRef<{ x: number; y: number; t: number } | null>(null);

  const trigger = useCallback(
    async (clientX: number, clientY: number, target: EventTarget | null) => {
      if (!(target instanceof Element)) return;
      if (target.closest(SKIP_SELECTOR)) return;
      const text = target.textContent?.trim() ?? '';
      if (!text || text.length > MAX_COPY_LENGTH) return;

      const ok = await copyToClipboard(text);
      if (!ok) return;

      setToast({ x: clientX, y: clientY, key: Date.now() });
    },
    []
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      trigger(e.clientX, e.clientY, e.target);
    },
    [trigger]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.changedTouches.length !== 1) return;
      const t = e.changedTouches[0];
      const now = Date.now();
      const prev = lastTap.current;
      if (
        prev &&
        now - prev.t < DOUBLE_TAP_MS &&
        Math.abs(t.clientX - prev.x) < DOUBLE_TAP_PX &&
        Math.abs(t.clientY - prev.y) < DOUBLE_TAP_PX
      ) {
        trigger(t.clientX, t.clientY, e.target);
        lastTap.current = null;
      } else {
        lastTap.current = { x: t.clientX, y: t.clientY, t: now };
      }
    },
    [trigger]
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return { toast, handleDoubleClick, handleTouchEnd };
}
